/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import cn from 'clsx';
import { Breadcrumbs, Link } from '@material-ui/core';
import { Modal, notification } from 'antd';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import Notification from 'components/Notification';
import {
  callComplianceDataAction,
  getComplianceTypes,
  getComplianceCustomOptions,
} from 'views/ProductDetail/utils';
import Block from './components/Block';
import AddDataModal, { ADD_DATA_ACTIONS } from './components/AddDataModal';
import Actions from './Actions';
import { useActions } from './selectorData';
import {
  getAddDataModalTitle,
  MODAL_DATA_KEY_MAPPING,
  MODAL_PAYLOAD_FN_KEY_MAPPING,
} from './utils';
import classes from './ComplianceData.module.scss';
import GenerateForms from './components/GenerateForms';
import { useFunctionBarActions } from 'views/ProductDetail/Sections/Documents/DocumentsTab/useFunctionBarActions';

const ComplianceDataSection = ({ className, product, productId, reloadCBCount }) => {
  const { handleUpdateDocument } = useFunctionBarActions(() => {});
  const { getV2CountryList, getProductAllFiles1 } = useActions();
  const [modal, contextHolder] = Modal.useModal();
  const [addDataAction, setAddDataAction] = useState('');
  const [editObj, setEditObj] = useState(null);
  const [testData, setTestData] = useState([]);
  const [certificateData, setCertificateData] = useState([]);
  const [otherData, setOtherData] = useState([]);
  const [newDataIds, setNewDataIds] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [complianceTypeOptions, setComplianceTypeOptions] = useState([]);
  const [otherComplianceTypeOptions, setOtherComplianceTypeOptions] = useState([]);
  const [issuePartyOptions, setIssuePartyOptions] = useState([]);
  const [scoreOptions, setScoreOptions] = useState([]);
  const [notification, setNotification] = useState({ type: null, message: null });
  const reloadTestData = async () => {
    const res = await callComplianceDataAction({
      key: 'test-data',
      action: 'get',
      id: productId,
      data: {},
    });
    setTestData(res?.data?.data || []);
  };
  const reloadCertificateData = async () => {
    const res = await callComplianceDataAction({
      key: 'certificate-data',
      action: 'get',
      id: productId,
      data: {},
    });
    setCertificateData(res?.data?.data || []);
  };
  const reloadOtherData = async () => {
    const res = await callComplianceDataAction({
      key: 'other-data',
      action: 'get',
      id: productId,
      data: {},
    });
    setOtherData(res?.data?.data || []);
  };
  const RELOAD_MAPPING = {
    [ADD_DATA_ACTIONS.TEST_DATA]: reloadTestData,
    [ADD_DATA_ACTIONS.CERTIFICATE_DATA]: reloadCertificateData,
    [ADD_DATA_ACTIONS.OTHER_DATA]: reloadOtherData,
  };
  const handleConfirm = ({ title, description, submitText }, cb) => {
    modal.confirm({
      title,
      icon: <></>,
      content: description,
      onOk() {
        if (cb) cb();
      },
      okText: submitText,
      closable: true,
      onCancel() {},
      centered: true,
      cancelButtonProps: {
        className: classes.cancelBtn,
      },
      className: classes.warningModal,
    });
  };
  const handleArchive = (tAction) => (record) => {
    handleConfirm(
      {
        title: 'Archive Data ?',
        description: 'Data will be archived and will no longer generate warnings.',
        submitText: 'Archive',
      },
      async () => {
        const res = await callComplianceDataAction({
          key: MODAL_DATA_KEY_MAPPING[tAction],
          action: 'archive',
          id: productId,
          data: {
            id: record.id,
          },
        });
        if (reloadCBCount) reloadCBCount();
        setNotification({
          type: res.success ? 'success' : 'error',
          message: res.success ? 'Archive Succeeded' : 'Archive Failed',
        });
        if (RELOAD_MAPPING[tAction]) RELOAD_MAPPING[tAction]();
      }
    );
  };
  const handleUnarchive = (tAction) => (record) => {
    handleConfirm(
      {
        title: 'Unarchive Data ?',
        description: 'Data will be unarchived and may generate warnings when it is expired.',
        submitText: 'Unarchive',
      },
      async () => {
        const res = await callComplianceDataAction({
          key: MODAL_DATA_KEY_MAPPING[tAction],
          action: 'unarchive',
          id: productId,
          data: {
            id: record.id,
          },
        });
        if (reloadCBCount) reloadCBCount();
        setNotification({
          type: res.success ? 'success' : 'error',
          message: res.success ? 'Unarchive Succeeded' : 'Unarchive Failed',
        });
        if (RELOAD_MAPPING[tAction]) RELOAD_MAPPING[tAction]();
      }
    );
  };
  const handleDelete = (tAction) => (record) => {
    handleConfirm(
      {
        title: <>Are you sure to remove data in this product?</>,
        description: 'The action cannot be undone.',
        submitText: 'Remove',
      },
      async () => {
        const res = await callComplianceDataAction({
          key: MODAL_DATA_KEY_MAPPING[tAction],
          action: 'delete',
          id: productId,
          data: {
            id: record.id,
          },
        });
        if (reloadCBCount) reloadCBCount();
        setNotification({
          type: res.success ? 'success' : 'error',
          message: res.success ? 'Data deleted' : 'Delete Failed',
        });
        if (RELOAD_MAPPING[tAction]) RELOAD_MAPPING[tAction]();
      }
    );
  };
  const handleEdit = (tAction) => (record) => {
    setEditObj({
      ...record,
      action: 'edit',
    });
    setAddDataAction(tAction);
  };
  const handleDuplicate = (tAction) => (record) => {
    setEditObj({
      ...record,
      action: 'duplicate',
    });
    setAddDataAction(tAction);
  };
  const handleRenew = (tAction) => (record) => {
    setEditObj({
      ...record,
      action: 'renew',
    });
    setAddDataAction(tAction);
  };
  const handleAddData = async ({ isRenew, ...tValues }, cb) => {
    const tPayloadFn = MODAL_PAYLOAD_FN_KEY_MAPPING[addDataAction];
    if (tPayloadFn) {
      const res = await callComplianceDataAction({
        key: MODAL_DATA_KEY_MAPPING[addDataAction],
        action: isRenew ? 'renew' : 'add',
        id: productId,
        data: tPayloadFn(tValues),
      });
      if (res.success) {
        if (isRenew || (!isRenew && !tValues.id)) {
          const newId =
            res?.data?.data?.id || (typeof res?.data?.data === 'number' ? res?.data?.data : '');
          if (newId) {
            setNewDataIds((prev) => [...prev, newId]);
          }
        }
        if (reloadCBCount) reloadCBCount();
        if (RELOAD_MAPPING[addDataAction]) RELOAD_MAPPING[addDataAction]();
        setNotification({
          type: 'success',
          message: isRenew ? 'Renew Succeeded' : tValues.id ? 'Data Updated' : 'Data Created',
        });
        setAddDataAction('');
        setEditObj(null);
      } else {
        setNotification({
          type: 'error',
          message: isRenew
            ? 'Failed to renew'
            : tValues.id
            ? 'Failed to update Data'
            : 'Failed to create Data',
        });
      }
    }
    if (cb) cb();
  };
  const reloadIssuePartyOptions = async (cb) => {
    const res = await getComplianceCustomOptions('issueParty');
    setIssuePartyOptions(
      (res?.data || []).map((obj) => ({
        value: obj.id,
        label: obj.name,
      }))
    );
  };
  const reloadComplianceTypes = (tKey) => async (cb) => {
    if (tKey === ADD_DATA_ACTIONS.OTHER_DATA) {
      const res = await getComplianceCustomOptions('complianceOtherDataType');
      setOtherComplianceTypeOptions(
        (res?.data || []).map((obj) => ({
          value: obj.id,
          label: obj.name,
        }))
      );
    } else {
      const res = await getComplianceTypes();
      setComplianceTypeOptions(
        (res?.data || []).map((obj) => ({
          value: obj.id,
          label: obj.name,
        }))
      );
    }
    if (cb) cb();
  };
  const reloadScoreOptions = async (cb) => {
    const res = await getComplianceCustomOptions('certificateScore');
    setScoreOptions(
      (res?.data || []).map((obj) => ({
        value: obj.id,
        label: obj.name,
      }))
    );
    if (cb) cb();
  };

  const onUpdateDocument = (type) => (values, form, cb) => {
    const prefix = {
      test: 'Test Data',
      certificate: 'Certificate Data',
      other: 'Other Data',
    };
    handleUpdateDocument(values, form, ({ status }) => {
      if (cb) {
        cb();
      }
      if (status === 'success') {
        //Reload Data
        if (type === 'test') {
          reloadTestData();
        }
        if (type === 'certificate') {
          reloadCertificateData();
        }
        if (type === 'other') {
          reloadOtherData();
        }
      }
    });
  };

  useEffect(() => {
    getV2CountryList();
    reloadTestData();
    reloadCertificateData();
    reloadOtherData();
    reloadIssuePartyOptions();
    getProductAllFiles1(
      productId,
      {
        pageNo: 1,
        pageSize: 99999,
        status: 'active',
        viewMode: 'document',
      },
      (res) => {
        setExistingDocuments(res?.data?.data?.list || []);
      }
    );
    reloadComplianceTypes(ADD_DATA_ACTIONS.CERTIFICATE_DATA)();
    reloadComplianceTypes(ADD_DATA_ACTIONS.OTHER_DATA)();
    reloadScoreOptions();
  }, []);

  return (
    <GenerateForms product={product} productId={productId} reloadTestData={reloadTestData}>
      <div className={cn(classes.wrapper, className)}>
        <Breadcrumbs separator={<NavigateNextIcon />} className={classes.breadcrumb}>
          <Link color="inherit" href="/product/list">
            Product List
          </Link>
          <span>Compliance Data</span>
        </Breadcrumbs>
        <div className={classes.mainContent}>
          <div className={classes.header}>
            <h3>Compliance Data</h3>
            <Actions />
          </div>
          <div className={classes.list}>
            <Block
              title="Test Data"
              data={testData}
              onAdd={() => {
                setAddDataAction(ADD_DATA_ACTIONS.TEST_DATA);
              }}
              onArchive={handleArchive(ADD_DATA_ACTIONS.TEST_DATA)}
              onUnarchive={handleUnarchive(ADD_DATA_ACTIONS.TEST_DATA)}
              onDelete={handleDelete(ADD_DATA_ACTIONS.TEST_DATA)}
              onEdit={handleEdit(ADD_DATA_ACTIONS.TEST_DATA)}
              onDuplicate={handleDuplicate(ADD_DATA_ACTIONS.TEST_DATA)}
              onRenew={handleRenew(ADD_DATA_ACTIONS.TEST_DATA)}
              type={'test'}
              newDataIds={newDataIds}
              onUpdateDocument={onUpdateDocument('test')}
            />
            <Block
              title="Certificate Data"
              data={certificateData}
              onAdd={() => {
                setAddDataAction(ADD_DATA_ACTIONS.CERTIFICATE_DATA);
              }}
              onEdit={handleEdit(ADD_DATA_ACTIONS.CERTIFICATE_DATA)}
              onArchive={handleArchive(ADD_DATA_ACTIONS.CERTIFICATE_DATA)}
              onUnarchive={handleUnarchive(ADD_DATA_ACTIONS.CERTIFICATE_DATA)}
              onDelete={handleDelete(ADD_DATA_ACTIONS.CERTIFICATE_DATA)}
              onDuplicate={handleDuplicate(ADD_DATA_ACTIONS.CERTIFICATE_DATA)}
              onRenew={handleRenew(ADD_DATA_ACTIONS.CERTIFICATE_DATA)}
              type={'certificate'}
              newDataIds={newDataIds}
              onUpdateDocument={onUpdateDocument('certificate')}
            />
            <Block
              title="Other"
              data={otherData}
              onAdd={() => {
                setAddDataAction(ADD_DATA_ACTIONS.OTHER_DATA);
              }}
              onEdit={handleEdit(ADD_DATA_ACTIONS.OTHER_DATA)}
              onArchive={handleArchive(ADD_DATA_ACTIONS.OTHER_DATA)}
              onUnarchive={handleUnarchive(ADD_DATA_ACTIONS.OTHER_DATA)}
              onDelete={handleDelete(ADD_DATA_ACTIONS.OTHER_DATA)}
              onDuplicate={handleDuplicate(ADD_DATA_ACTIONS.OTHER_DATA)}
              onRenew={handleRenew(ADD_DATA_ACTIONS.OTHER_DATA)}
              type={'other'}
              newDataIds={newDataIds}
              onUpdateDocument={onUpdateDocument('other')}
            />
          </div>
        </div>
      </div>
      {!!addDataAction && (
        <AddDataModal
          open
          action={addDataAction}
          title={getAddDataModalTitle(addDataAction)}
          onClose={() => {
            setAddDataAction('');
            setEditObj(null);
          }}
          editObj={editObj}
          issuePartyOptions={issuePartyOptions}
          existingDocuments={existingDocuments}
          onSubmit={handleAddData}
          reloadIssuePartyOptions={reloadIssuePartyOptions}
          complianceTypeOptions={complianceTypeOptions}
          reloadComplianceTypes={reloadComplianceTypes(addDataAction)}
          scoreOptions={scoreOptions}
          reloadScoreOptions={reloadScoreOptions}
          otherComplianceTypeOptions={otherComplianceTypeOptions}
        />
      )}
      {contextHolder}
      <Notification
        notification={notification}
        onClose={() => setNotification({ type: null, message: null })}
        handleOnClose={() => setNotification({ type: null, message: null })}
      />
    </GenerateForms>
  );
};

export default ComplianceDataSection;
